import React, { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { Link, useSearchParams } from "react-router-dom";
import { API_BASE_URL } from "@/lib/apiBase";
import { browseChannels } from "@/lib/dmApi";
import {
  clearCircatronInstallationAvatar,
  clearIncomingWebhookAvatar,
  resolveAvatarUrl,
  setCircatronInstallationAvatarUrl,
  setIncomingWebhookAvatarUrl,
  uploadCircatronInstallationAvatar,
  uploadIncomingWebhookAvatar,
} from "@/lib/avatarApi";
import { useConfirm } from "@/components/ConfirmDialogProvider";
import AvatarSourceEditor from "@/components/avatars/AvatarSourceEditor";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toastEntitlementOrError } from "@/lib/entitlementToast";
import { toastError, toastSuccess } from "@/lib/toast";
import CircatronPackInstallWizard from "@/components/CircatronPackInstallWizard";
import MarkdownBody from "@/components/MarkdownBody";
import { circatronPipeLabel } from "@/lib/circatronPipeLabel";
import {
  CIRCATRON_SCOPE_PRESETS,
  matchScopePreset,
  scopesForPreset,
} from "@/lib/circatronScopePresets";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

const EVENT_OPTIONS = [
  { id: "member.joined", label: "Member joined" },
  { id: "post.created", label: "Post created" },
  { id: "post.critical", label: "Post marked critical" },
];

function formatWhen(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString();
  } catch {
    return String(value);
  }
}

function formatLastDelivery(ld) {
  if (!ld) return "No deliveries yet";
  const status = String(ld.status || "unknown");
  const label =
    status === "success"
      ? "Delivered"
      : status === "pending"
        ? "Queued"
        : status === "retrying"
          ? "Retrying"
          : status === "dead" || status === "failure"
            ? "Failed"
            : status;
  const when = formatWhen(ld.last_attempt_at || ld.created_at);
  const err =
    ld.error_snippet && status !== "success"
      ? ` — ${String(ld.error_snippet).slice(0, 100)}`
      : "";
  return `${label} · ${when}${err}`;
}

const APP_SCOPE_OPTIONS = [
  { id: "channel.post", label: "Post to bound channel", needsTeams: false },
  { id: "posts.read.team", label: "Read team posts", needsTeams: true },
  { id: "posts.read.org", label: "Read org feed", needsTeams: false },
  { id: "posts.write.team", label: "Create team posts", needsTeams: true },
  { id: "posts.write.org", label: "Create org-wide posts", needsTeams: false },
  { id: "kanban.update", label: "Create/update kanban cards", needsTeams: true },
];

function scopesNeedTeams(scopes) {
  return APP_SCOPE_OPTIONS.some((s) => s.needsTeams && scopes.includes(s.id));
}

/**
 * Settings → Circatrons (Slice A–C).
 * Pro+ modules.connectors; Free sees upgrade CTA.
 * Integrations admins see full Connectors; members manage personal bots under Profile → My bots.
 */
const OrgConnectorsPanel = ({
  orgId,
  accessToken,
  canIntegrations = true,
}) => {
  const confirm = useConfirm();
  const [searchParams] = useSearchParams();
  const deepLinkInstallHandled = useRef(null);
  const deepLinkPackHandled = useRef(null);
  const appsSectionRef = useRef(null);
  const [entLoading, setEntLoading] = useState(true);
  const [modules, setModules] = useState(null);
  const [outbound, setOutbound] = useState([]);
  const [incoming, setIncoming] = useState([]);
  const [installations, setInstallations] = useState([]);
  const [channels, setChannels] = useState([]);
  const [teams, setTeams] = useState([]);
  const [packs, setPacks] = useState([]);
  const [marketplaceListings, setMarketplaceListings] = useState([]);
  const [browseOpen, setBrowseOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [revealedHardInbound, setRevealedHardInbound] = useState(null);
  const [editingInstallId, setEditingInstallId] = useState(null);
  const [editInstallName, setEditInstallName] = useState("");
  const [editInstallChannelId, setEditInstallChannelId] = useState("");
  const [editInstallScopes, setEditInstallScopes] = useState([]);
  const [editInstallScopePreset, setEditInstallScopePreset] =
    useState("channel_bot");
  const [editInstallTeamIds, setEditInstallTeamIds] = useState([]);
  const [savingInstall, setSavingInstall] = useState(false);

  const [outName, setOutName] = useState("");
  const [outUrl, setOutUrl] = useState("");
  const [outEvents, setOutEvents] = useState(["post.critical"]);
  const [creatingOut, setCreatingOut] = useState(false);
  const [revealedSecret, setRevealedSecret] = useState(null);

  const [inName, setInName] = useState("");
  const [inChannelId, setInChannelId] = useState("");
  const [creatingIn, setCreatingIn] = useState(false);
  const [revealedIncoming, setRevealedIncoming] = useState(null);
  const [editingWebhookId, setEditingWebhookId] = useState(null);
  const [editName, setEditName] = useState("");
  const [savingEdit, setSavingEdit] = useState(false);
  const [testingWebhookId, setTestingWebhookId] = useState(null);

  const [installingPackId, setInstallingPackId] = useState(null);
  const [installPackDetail, setInstallPackDetail] = useState(null);
  const [loadingPackId, setLoadingPackId] = useState(null);
  const [viewPackDetail, setViewPackDetail] = useState(null);
  const [viewLoadingPackId, setViewLoadingPackId] = useState(null);
  const [packInstallResult, setPackInstallResult] = useState(null);
  const [createIncomingOpen, setCreateIncomingOpen] = useState(false);
  const [createOutboundOpen, setCreateOutboundOpen] = useState(false);
  const [personalBotsEnabled, setPersonalBotsEnabled] = useState(false);
  const [savingPersonalBots, setSavingPersonalBots] = useState(false);

  const authHeaders = useCallback(
    () => ({ Authorization: `Bearer ${accessToken}` }),
    [accessToken]
  );

  useEffect(() => {
    if (!orgId || !accessToken) return;
    let cancelled = false;
    (async () => {
      setEntLoading(true);
      try {
        const res = await axios.get(`${API_BASE_URL}/org/${orgId}/entitlements`, {
          headers: authHeaders(),
        });
        if (!cancelled) setModules(res.data?.modules || {});
      } catch (err) {
        console.error(err);
        if (!cancelled) setModules({});
      } finally {
        if (!cancelled) setEntLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [orgId, accessToken, authHeaders]);

  const connectorsEnabled = Boolean(modules?.connectors);

  const loadLists = useCallback(async () => {
    if (!orgId || !accessToken || !connectorsEnabled || !canIntegrations) return;
    setLoading(true);
    setError(null);
    try {
      const [
        outRes,
        inRes,
        packsRes,
        instRes,
        metaRes,
        ch,
        orgTeams,
        requestableTeams,
        memberTeams,
        marketRes,
      ] = await Promise.all([
        axios.get(`${API_BASE_URL}/org/${orgId}/connectors/outbound`, {
          headers: authHeaders(),
        }),
        axios.get(`${API_BASE_URL}/org/${orgId}/connectors/incoming`, {
          headers: authHeaders(),
        }),
        axios.get(`${API_BASE_URL}/org/${orgId}/connectors/packs`, {
          headers: authHeaders(),
        }),
        axios
          .get(`${API_BASE_URL}/org/${orgId}/connectors/installations`, {
            headers: authHeaders(),
          })
          .catch((err) => {
            console.error("Failed to load Circatron installations", err);
            return { data: { installations: [] } };
          }),
        axios
          .get(`${API_BASE_URL}/org/${orgId}/connectors/meta`, {
            headers: authHeaders(),
          })
          .catch(() => ({ data: {} })),
        browseChannels(accessToken, orgId),
        axios
          .get(`${API_BASE_URL}/team/organization`, {
            headers: authHeaders(),
            params: { org_id: orgId },
          })
          .catch(() => ({ data: [] })),
        axios
          .get(`${API_BASE_URL}/team/requestable`, {
            headers: authHeaders(),
            params: { org_id: orgId },
          })
          .catch(() => ({ data: [] })),
        axios
          .get(`${API_BASE_URL}/team/memberships`, {
            headers: authHeaders(),
            params: { org_id: orgId },
          })
          .catch(() => ({ data: [] })),
        axios
          .get(`${API_BASE_URL}/marketplace/circatrons`)
          .catch(() => ({ data: { listings: [] } })),
      ]);
      setPersonalBotsEnabled(Boolean(metaRes.data?.personal_bots?.enabled));
      setOutbound(
        Array.isArray(outRes.data?.endpoints) ? outRes.data.endpoints : []
      );
      setIncoming(
        Array.isArray(inRes.data?.webhooks) ? inRes.data.webhooks : []
      );
      setInstallations(
        Array.isArray(instRes.data?.installations)
          ? instRes.data.installations
          : []
      );
      setPacks(Array.isArray(packsRes.data?.packs) ? packsRes.data.packs : []);
      setMarketplaceListings(
        Array.isArray(marketRes.data?.listings) ? marketRes.data.listings : []
      );
      setChannels(Array.isArray(ch) ? ch : []);
      const teamMap = new Map();
      for (const t of [
        ...(Array.isArray(orgTeams.data) ? orgTeams.data : []),
        ...(Array.isArray(requestableTeams.data) ? requestableTeams.data : []),
        ...(Array.isArray(memberTeams.data) ? memberTeams.data : []),
      ]) {
        const id = Number(t.team_id ?? t.teamId);
        if (Number.isFinite(id) && id > 0) teamMap.set(id, t);
      }
      setTeams([...teamMap.values()]);
    } catch (err) {
      console.error(err);
      if (
        toastEntitlementOrError(err, "Failed to load connectors", {
          canManageSettings: true,
          orgId,
        })
      ) {
        setError(null);
      } else {
        setError(err.response?.data?.message || "Failed to load connectors");
      }
    } finally {
      setLoading(false);
    }
  }, [orgId, accessToken, connectorsEnabled, canIntegrations, authHeaders]);

  const togglePersonalBots = async (enabled) => {
    setSavingPersonalBots(true);
    try {
      const res = await axios.patch(
        `${API_BASE_URL}/org/${orgId}/connectors/personal-bots`,
        { enabled: Boolean(enabled) },
        { headers: authHeaders() }
      );
      setPersonalBotsEnabled(Boolean(res.data?.personal_bots?.enabled));
      toastSuccess(
        enabled ? "Personal bots enabled" : "Personal bots disabled"
      );
    } catch (err) {
      toastEntitlementOrError(err, "Failed to update personal bots policy", {
        canManageSettings: true,
        orgId,
      });
    } finally {
      setSavingPersonalBots(false);
    }
  };

  const fetchPackDetail = useCallback(
    async (packId) => {
      const res = await axios.get(
        `${API_BASE_URL}/org/${orgId}/connectors/packs/${encodeURIComponent(
          packId
        )}`,
        { headers: authHeaders() }
      );
      return res.data?.pack || null;
    },
    [orgId, authHeaders]
  );

  const startPackInstall = useCallback(
    async (packSummaryOrDetail) => {
      setPackInstallResult(null);
      setViewPackDetail(null);
      const packId = packSummaryOrDetail.id;
      // Already have full manifest (e.g. from View modal)
      if (packSummaryOrDetail.docs_markdown != null) {
        setInstallPackDetail(packSummaryOrDetail);
        setInstallingPackId(packId);
        return;
      }
      setLoadingPackId(packId);
      try {
        const pack = await fetchPackDetail(packId);
        setInstallPackDetail(pack);
        setInstallingPackId(packId);
      } catch (err) {
        toastEntitlementOrError(err, "Failed to load pack", {
          canManageSettings: true,
          orgId,
        });
      } finally {
        setLoadingPackId(null);
      }
    },
    [fetchPackDetail, orgId]
  );

  const openPackView = async (packSummary) => {
    setViewLoadingPackId(packSummary.id);
    try {
      const pack = await fetchPackDetail(packSummary.id);
      setViewPackDetail(pack);
    } catch (err) {
      toastEntitlementOrError(err, "Failed to load pack", {
        canManageSettings: true,
        orgId,
      });
    } finally {
      setViewLoadingPackId(null);
    }
  };

  const onPackInstalled = async (result) => {
    setInstallingPackId(null);
    setInstallPackDetail(null);
    setPackInstallResult(result || null);
    if (result?.kind === "outbound" && result.signing_secret) {
      setRevealedSecret(result.signing_secret);
      setRevealedIncoming(null);
      setRevealedHardInbound(null);
    }
    if (result?.kind === "incoming" && result.url) {
      setRevealedIncoming({ url: result.url, token: result.token });
      setRevealedSecret(null);
      setRevealedHardInbound(null);
    }
    if (result?.kind === "hard_inbound") {
      setRevealedHardInbound({
        installation_id: result.installation_id,
        webhook_url: result.webhook_url,
        signing_secret: result.signing_secret,
        bot_token: result.bot_token,
      });
      setRevealedSecret(null);
      setRevealedIncoming(null);
    }
    await loadLists();
  };

  const revokeInstallation = async (inst) => {
    const ok = await confirm({
      title: "Revoke app installation?",
      description:
        "Inbound webhooks and bot tokens for this Circatron stop working immediately.",
      confirmLabel: "Revoke",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await axios.delete(
        `${API_BASE_URL}/org/${orgId}/connectors/installations/${inst.installation_id}`,
        { headers: authHeaders() }
      );
      toastSuccess("Installation revoked");
      setEditingInstallId(null);
      await loadLists();
    } catch (err) {
      toastEntitlementOrError(err, "Failed to revoke installation", {
        canManageSettings: true,
        orgId,
      });
    }
  };

  const startEditInstall = (inst) => {
    setEditingInstallId(inst.installation_id);
    setEditInstallName(inst.bot?.display_name || inst.provider_id || "");
    setEditInstallChannelId(
      inst.conversation_id != null ? String(inst.conversation_id) : ""
    );
    const nextScopes =
      Array.isArray(inst.scopes) && inst.scopes.length
        ? [...inst.scopes]
        : ["channel.post"];
    setEditInstallScopes(nextScopes);
    setEditInstallScopePreset(matchScopePreset(nextScopes));
    setEditInstallTeamIds(
      Array.isArray(inst.team_ids)
        ? inst.team_ids.map(Number).filter((n) => Number.isFinite(n) && n > 0)
        : []
    );
  };

  const applyEditInstallPreset = (presetId) => {
    setEditInstallScopePreset(presetId);
    if (presetId === "custom") return;
    const next = scopesForPreset(presetId);
    if (next) setEditInstallScopes(next);
  };

  const toggleEditInstallScope = (id) => {
    setEditInstallScopes((prev) => {
      const next = prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [...prev, id];
      setEditInstallScopePreset(matchScopePreset(next));
      return next;
    });
  };

  const toggleEditInstallTeam = (id) => {
    const n = Number(id);
    setEditInstallTeamIds((prev) =>
      prev.includes(n) ? prev.filter((x) => x !== n) : [...prev, n]
    );
  };

  const saveEditInstall = async (e) => {
    e?.preventDefault?.();
    if (!editingInstallId) return;
    if (!editInstallScopes.length) {
      toastError(null, "Select at least one scope");
      return;
    }
    if (
      scopesNeedTeams(editInstallScopes) &&
      editInstallTeamIds.length === 0
    ) {
      toastError(null, "Select at least one team for team/kanban scopes");
      return;
    }
    const conversationId = Number(editInstallChannelId);
    if (!Number.isFinite(conversationId) || conversationId < 1) {
      toastError(null, "Select a channel");
      return;
    }
    setSavingInstall(true);
    try {
      await axios.patch(
        `${API_BASE_URL}/org/${orgId}/connectors/installations/${editingInstallId}`,
        {
          name: editInstallName.trim() || "Circatron",
          conversation_id: conversationId,
          scopes: editInstallScopes,
          team_ids: editInstallTeamIds,
        },
        { headers: authHeaders() }
      );
      toastSuccess("App updated");
      setEditingInstallId(null);
      await loadLists();
    } catch (err) {
      toastEntitlementOrError(err, "Failed to update app", {
        canManageSettings: true,
        orgId,
      });
    } finally {
      setSavingInstall(false);
    }
  };

  const patchInstallInList = (updated) => {
    const id = Number(
      updated?.installation_id ?? updated?.bot?.installation_id
    );
    if (!Number.isFinite(id)) return;
    setInstallations((prev) =>
      prev.map((row) => {
        if (Number(row.installation_id) !== id) return row;
        const next = { ...row, ...updated };
        if (updated?.avatar_url != null || updated?.display_name != null) {
          next.bot = {
            ...(row.bot || {}),
            ...(updated.bot || {}),
            display_name:
              updated.display_name ??
              updated.bot?.display_name ??
              row.bot?.display_name,
            avatar_url:
              updated.avatar_url ??
              updated.bot?.avatar_url ??
              row.bot?.avatar_url,
          };
        }
        return next;
      })
    );
  };

  const rotateInstallBotToken = async (inst) => {
    const ok = await confirm({
      title: "Rotate bot token?",
      description:
        "The current bot token stops working immediately. Copy the new token after rotating — it is shown only once.",
      confirmLabel: "Rotate token",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      const res = await axios.post(
        `${API_BASE_URL}/org/${orgId}/connectors/installations/${inst.installation_id}/rotate-bot-token`,
        {},
        { headers: authHeaders() }
      );
      setRevealedHardInbound({
        installation_id: inst.installation_id,
        webhook_url: null,
        signing_secret: null,
        bot_token: res.data?.bot_token,
      });
      setRevealedSecret(null);
      setRevealedIncoming(null);
      toastSuccess("Bot token rotated — copy it now");
    } catch (err) {
      toastEntitlementOrError(err, "Failed to rotate bot token", {
        canManageSettings: true,
        orgId,
      });
    }
  };

  const rotateInstallSigningSecret = async (inst) => {
    const ok = await confirm({
      title: "Rotate signing secret?",
      description:
        "Inbound webhooks must use the new secret immediately. Copy it after rotating — it is shown only once.",
      confirmLabel: "Rotate secret",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      const res = await axios.post(
        `${API_BASE_URL}/org/${orgId}/connectors/installations/${inst.installation_id}/rotate-signing-secret`,
        {},
        { headers: authHeaders() }
      );
      setRevealedHardInbound({
        installation_id: inst.installation_id,
        webhook_url: res.data?.webhook_url || null,
        signing_secret: res.data?.signing_secret,
        bot_token: null,
      });
      setRevealedSecret(null);
      setRevealedIncoming(null);
      toastSuccess("Signing secret rotated — copy it now");
    } catch (err) {
      toastEntitlementOrError(err, "Failed to rotate signing secret", {
        canManageSettings: true,
        orgId,
      });
    }
  };

  useEffect(() => {
    if (connectorsEnabled) loadLists();
  }, [connectorsEnabled, loadLists]);

  // Deep-link: ?installation=<id> opens Apps edit after list load.
  useEffect(() => {
    if (!connectorsEnabled || loading) return;
    const raw = searchParams.get("installation");
    if (!raw) return;
    const id = Number(raw);
    if (!Number.isFinite(id) || id < 1) return;
    if (deepLinkInstallHandled.current === id) return;
    const inst = installations.find(
      (i) => Number(i.installation_id) === id && String(i.status) === "active"
    );
    if (!inst) return;
    deepLinkInstallHandled.current = id;
    startEditInstall(inst);
    requestAnimationFrame(() => {
      appsSectionRef.current?.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    });
  }, [connectorsEnabled, loading, installations, searchParams]);

  // Deep-link: ?pack=<id> opens install wizard after packs load.
  useEffect(() => {
    if (!connectorsEnabled || loading) return;
    const packId = String(searchParams.get("pack") || "").trim();
    if (!packId) return;
    if (deepLinkPackHandled.current === packId) return;
    const summary = packs.find((p) => p.id === packId);
    if (!summary && packs.length === 0) return;
    deepLinkPackHandled.current = packId;
    void startPackInstall(summary || { id: packId });
  }, [connectorsEnabled, loading, packs, searchParams, startPackInstall]);

  const toggleEvent = (id) => {
    setOutEvents((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const createOutbound = async (e) => {
    e.preventDefault();
    if (!outUrl.trim() || outEvents.length === 0) {
      toastError(null, "URL and at least one event are required");
      return;
    }
    setCreatingOut(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/org/${orgId}/connectors/outbound`,
        {
          name: outName.trim() || "Event webhook",
          target_url: outUrl.trim(),
          events: outEvents,
          enabled: true,
        },
        { headers: authHeaders() }
      );
      setRevealedSecret(res.data?.signing_secret || null);
      setPackInstallResult(null);
      setOutName("");
      setOutUrl("");
      setCreateOutboundOpen(false);
      toastSuccess("Event webhook created — copy the signing secret now");
      await loadLists();
    } catch (err) {
      toastEntitlementOrError(err, "Failed to create event webhook", {
        canManageSettings: true,
        orgId,
      });
    } finally {
      setCreatingOut(false);
    }
  };

  const toggleOutbound = async (ep) => {
    try {
      await axios.patch(
        `${API_BASE_URL}/org/${orgId}/connectors/outbound/${ep.endpoint_id}`,
        { enabled: !ep.enabled },
        { headers: authHeaders() }
      );
      await loadLists();
    } catch (err) {
      toastEntitlementOrError(err, "Failed to update connector", {
        canManageSettings: true,
        orgId,
      });
    }
  };

  const rotateOutbound = async (ep) => {
    try {
      const res = await axios.post(
        `${API_BASE_URL}/org/${orgId}/connectors/outbound/${ep.endpoint_id}/rotate`,
        {},
        { headers: authHeaders() }
      );
      setRevealedSecret(res.data?.signing_secret || null);
      toastSuccess("Signing secret rotated — copy it now");
    } catch (err) {
      toastEntitlementOrError(err, "Failed to rotate secret", {
        canManageSettings: true,
        orgId,
      });
    }
  };

  const deleteOutbound = async (ep) => {
    try {
      await axios.delete(
        `${API_BASE_URL}/org/${orgId}/connectors/outbound/${ep.endpoint_id}`,
        { headers: authHeaders() }
      );
      toastSuccess("Event webhook deleted");
      await loadLists();
    } catch (err) {
      toastEntitlementOrError(err, "Failed to delete connector", {
        canManageSettings: true,
        orgId,
      });
    }
  };

  const createIncoming = async (e) => {
    e.preventDefault();
    const conversationId = Number(inChannelId);
    if (!Number.isFinite(conversationId) || conversationId < 1) {
      toastError(null, "Select a channel");
      return;
    }
    setCreatingIn(true);
    try {
      const res = await axios.post(
        `${API_BASE_URL}/org/${orgId}/connectors/incoming`,
        {
          conversation_id: conversationId,
          name: inName.trim() || "Channel webhook",
        },
        { headers: authHeaders() }
      );
      setRevealedIncoming({
        url: res.data?.url,
        token: res.data?.token,
      });
      setPackInstallResult(null);
      setInName("");
      setInChannelId("");
      setCreateIncomingOpen(false);
      toastSuccess("Channel webhook created — copy the URL now");
      await loadLists();
    } catch (err) {
      toastEntitlementOrError(err, "Failed to create channel webhook", {
        canManageSettings: true,
        orgId,
      });
    } finally {
      setCreatingIn(false);
    }
  };

  const channelLabel = (wh) => {
    const fromApi = String(wh?.channel_title || "").trim();
    if (fromApi) return fromApi;
    const id = Number(wh?.conversation_id);
    const fromBrowse = channels.find(
      (c) => Number(c.conversation_id) === id
    );
    if (fromBrowse?.title) return fromBrowse.title;
    if (fromBrowse?.team_name) return fromBrowse.team_name;
    return Number.isFinite(id) ? `Channel ${id}` : "Channel";
  };

  const activeInstallations = installations.filter(
    (i) => String(i.status || "") === "active" && !i.revoked_at
  );
  const orgActiveInstallations = activeInstallations.filter(
    (i) => String(i.install_kind || "org") !== "personal"
  );
  const personalActiveInstallations = activeInstallations.filter(
    (i) => String(i.install_kind || "") === "personal"
  );

  const rotateIncoming = async (wh) => {
    const ok = await confirm({
      title: "Rotate webhook URL?",
      description:
        "The current URL stops working immediately. Copy the new URL after rotating — it is shown only once.",
      confirmLabel: "Rotate URL",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      const res = await axios.post(
        `${API_BASE_URL}/org/${orgId}/connectors/incoming/${wh.webhook_id}/rotate`,
        {},
        { headers: authHeaders() }
      );
      setRevealedIncoming({
        url: res.data?.url,
        token: res.data?.token,
      });
      toastSuccess("Webhook URL rotated — copy it now");
      await loadLists();
    } catch (err) {
      toastEntitlementOrError(err, "Failed to rotate webhook", {
        canManageSettings: true,
        orgId,
      });
    }
  };

  const revokeIncoming = async (wh) => {
    const ok = await confirm({
      title: "Revoke channel webhook?",
      description: `“${wh.name || "Webhook"}” on ${channelLabel(wh)} will stop accepting posts. This cannot be undone — create a new webhook if you need another URL.`,
      confirmLabel: "Revoke",
      variant: "destructive",
    });
    if (!ok) return;
    try {
      await axios.delete(
        `${API_BASE_URL}/org/${orgId}/connectors/incoming/${wh.webhook_id}`,
        { headers: authHeaders() }
      );
      toastSuccess("Channel webhook revoked");
      await loadLists();
    } catch (err) {
      toastEntitlementOrError(err, "Failed to revoke webhook", {
        canManageSettings: true,
        orgId,
      });
    }
  };

  const startEditIncoming = (wh) => {
    setEditingWebhookId(wh.webhook_id);
    setEditName(wh.name || "");
  };

  const saveEditIncoming = async (e) => {
    e?.preventDefault?.();
    if (!editingWebhookId) return;
    setSavingEdit(true);
    try {
      await axios.patch(
        `${API_BASE_URL}/org/${orgId}/connectors/incoming/${editingWebhookId}`,
        {
          name: editName.trim() || "Channel webhook",
        },
        { headers: authHeaders() }
      );
      toastSuccess("Webhook updated");
      setEditingWebhookId(null);
      await loadLists();
    } catch (err) {
      toastEntitlementOrError(err, "Failed to update webhook", {
        canManageSettings: true,
        orgId,
      });
    } finally {
      setSavingEdit(false);
    }
  };

  const patchIncomingInList = (updated) => {
    if (!updated?.webhook_id) return;
    setIncoming((prev) =>
      prev.map((w) =>
        w.webhook_id === updated.webhook_id ? { ...w, ...updated } : w
      )
    );
  };

  const testIncoming = async (wh) => {
    setTestingWebhookId(wh.webhook_id);
    try {
      await axios.post(
        `${API_BASE_URL}/org/${orgId}/connectors/incoming/${wh.webhook_id}/test`,
        {},
        { headers: authHeaders() }
      );
      toastSuccess("Test message posted to channel");
    } catch (err) {
      toastEntitlementOrError(err, "Failed to send test message", {
        canManageSettings: true,
        orgId,
      });
    } finally {
      setTestingWebhookId(null);
    }
  };

  const copyText = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toastSuccess(`${label} copied`);
    } catch {
      toastError(null, `Could not copy ${label}`);
    }
  };

  const circatronsHeader = (
    <div>
      <h3 className="text-base font-semibold">Circatrons</h3>
      <p className="mt-1 text-sm text-muted-foreground">
        Circatrons are integrations for your workplace — bots that post into
        channels, webhooks that notify your tools, and guided setups for CI,
        Slack, and more.
      </p>
    </div>
  );

  if (entLoading) {
    return (
      <div className="max-w-3xl">
        <p className="text-sm text-muted-foreground">Loading…</p>
      </div>
    );
  }

  if (!connectorsEnabled) {
    return (
      <div className="max-w-3xl space-y-6">
        {circatronsHeader}
        <div className="rounded-lg border border-border/80 bg-muted/30 px-4 py-4 text-sm text-muted-foreground space-y-3">
          <p>
            Circatrons require{" "}
            <span className="font-medium text-foreground">Pro</span> or higher.
          </p>
          <p>
            <Link
              to={`/orgs/${encodeURIComponent(orgId)}/settings?section=plan`}
              className="text-foreground underline underline-offset-2"
            >
              Upgrade in Plan &amp; usage
            </Link>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-3xl space-y-10">
      {circatronsHeader}

      <section className="rounded-lg border border-border/80 px-4 py-3 space-y-2">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h4 className="text-sm font-semibold">Allow personal bots</h4>
            <p className="mt-0.5 text-xs text-muted-foreground">
              When on, members with the org.personal_bots capability can create
              one personal install (off by default). Grant the capability in
              Members. Members manage their bots under{" "}
              <Link
                to="/profile?tab=bots"
                className="text-foreground underline underline-offset-2"
              >
                Profile → My bots
              </Link>
              .
            </p>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={personalBotsEnabled}
              disabled={savingPersonalBots || loading}
              onChange={(e) => togglePersonalBots(e.target.checked)}
            />
            {personalBotsEnabled ? "Enabled" : "Disabled"}
          </label>
        </div>
      </section>

      {revealedSecret ? (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm space-y-2">
          <p className="font-medium text-foreground">
            Signing secret (shown once)
          </p>
          {packInstallResult?.success_copy ? (
            <p className="text-xs text-muted-foreground">
              {packInstallResult.success_copy}
            </p>
          ) : null}
          <code className="block break-all text-xs">{revealedSecret}</code>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => copyText(revealedSecret, "Signing secret")}
          >
            Copy secret
          </Button>
        </div>
      ) : null}

      {revealedIncoming?.url ? (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm space-y-2">
          <p className="font-medium text-foreground">
            Webhook URL (shown once)
          </p>
          {packInstallResult?.success_copy ? (
            <p className="text-xs text-muted-foreground">
              {packInstallResult.success_copy}
            </p>
          ) : null}
          <code className="block break-all text-xs">{revealedIncoming.url}</code>
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => copyText(revealedIncoming.url, "Webhook URL")}
          >
            Copy URL
          </Button>
        </div>
      ) : null}

      {revealedHardInbound &&
      (revealedHardInbound.webhook_url ||
        revealedHardInbound.signing_secret ||
        revealedHardInbound.bot_token) ? (
        <div className="rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-3 text-sm space-y-3">
          <p className="font-medium text-foreground">
            App secrets (shown once)
          </p>
          {packInstallResult?.success_copy ? (
            <p className="text-xs text-muted-foreground">
              {packInstallResult.success_copy}
            </p>
          ) : null}
          {revealedHardInbound.installation_id != null ? (
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Installation id</p>
              <code className="block break-all text-xs">
                {revealedHardInbound.installation_id}
              </code>
            </div>
          ) : null}
          {revealedHardInbound.webhook_url ? (
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Webhook URL</p>
              <code className="block break-all text-xs">
                {revealedHardInbound.webhook_url}
              </code>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() =>
                  copyText(revealedHardInbound.webhook_url, "Webhook URL")
                }
              >
                Copy URL
              </Button>
            </div>
          ) : null}
          {revealedHardInbound.signing_secret ? (
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Signing secret</p>
              <code className="block break-all text-xs">
                {revealedHardInbound.signing_secret}
              </code>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() =>
                  copyText(
                    revealedHardInbound.signing_secret,
                    "Signing secret"
                  )
                }
              >
                Copy secret
              </Button>
            </div>
          ) : null}
          {revealedHardInbound.bot_token ? (
            <div className="space-y-1">
              <p className="text-xs text-muted-foreground">Bot token</p>
              <code className="block break-all text-xs">
                {revealedHardInbound.bot_token}
              </code>
              <Button
                type="button"
                size="sm"
                variant="outline"
                onClick={() =>
                  copyText(revealedHardInbound.bot_token, "Bot token")
                }
              >
                Copy bot token
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      {packInstallResult?.docs_markdown ? (
        <div className="rounded-lg border border-border/80 px-4 py-3 space-y-2">
          <p className="text-sm font-medium">Setup guide</p>
          <div className="max-h-80 overflow-y-auto text-muted-foreground">
            <MarkdownBody
              content={packInstallResult.docs_markdown}
              compact
            />
          </div>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => setPackInstallResult(null)}
          >
            Dismiss
          </Button>
        </div>
      ) : null}

      {error ? (
        <p className="text-sm text-destructive">{error}</p>
      ) : null}

      <section className="space-y-6">
        {(() => {
          const packById = new Map(packs.map((p) => [p.id, p]));
          const listingToCard = (l) => ({
            id: l.pack_id,
            title: l.title,
            summary: l.summary,
            author: l.author,
            pipe: l.pipe,
          });
          const packToCard = (p) => ({
            id: p.id,
            title: p.title,
            summary: p.summary,
            author: "Circa",
            pipe: p.pipe,
          });
          const featuredCards =
            marketplaceListings.length > 0
              ? marketplaceListings.filter((l) => l.featured).map(listingToCard)
              : [];
          const allCards =
            marketplaceListings.length > 0
              ? marketplaceListings.map(listingToCard)
              : packs.map(packToCard);
          const featuredFallback =
            featuredCards.length > 0
              ? featuredCards
              : marketplaceListings.length === 0
                ? packs.map(packToCard)
                : [];

          const renderShelf = (cards, emptyCopy) => {
            if (loading && cards.length === 0) {
              return (
                <p className="text-sm text-muted-foreground">Loading…</p>
              );
            }
            if (cards.length === 0) {
              return (
                <p className="text-sm text-muted-foreground">{emptyCopy}</p>
              );
            }
            return (
              <ul className="grid gap-3 sm:grid-cols-2">
                {cards.map((p) => {
                  const pipeLabel = circatronPipeLabel(p.pipe);
                  return (
                    <li
                      key={p.id}
                      className="rounded-lg border border-border/80 px-3 py-3 space-y-2 text-sm"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0">
                          <p className="font-medium">{p.title}</p>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {p.summary}
                          </p>
                          {p.author || pipeLabel ? (
                            <p className="text-[11px] text-muted-foreground mt-1">
                              {[p.author, pipeLabel]
                                .filter(Boolean)
                                .join(" · ")}
                            </p>
                          ) : null}
                        </div>
                        <div className="flex flex-shrink-0 flex-wrap gap-2">
                          <Button
                            type="button"
                            size="sm"
                            variant="ghost"
                            disabled={viewLoadingPackId === p.id}
                            onClick={() =>
                              openPackView(packById.get(p.id) || { id: p.id })
                            }
                          >
                            {viewLoadingPackId === p.id ? "…" : "View"}
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="outline"
                            disabled={loadingPackId === p.id}
                            onClick={() =>
                              startPackInstall(
                                packById.get(p.id) || { id: p.id }
                              )
                            }
                          >
                            {loadingPackId === p.id
                              ? "…"
                              : installingPackId === p.id
                                ? "Open"
                                : "Install"}
                          </Button>
                        </div>
                      </div>
                    </li>
                  );
                })}
              </ul>
            );
          };

          return (
            <>
              <div className="space-y-3">
                <div className="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <h4 className="text-sm font-semibold">Featured</h4>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      Curated setups from Circa. Browse the full marketplace for
                      more.
                    </p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => setBrowseOpen((v) => !v)}
                  >
                    {browseOpen ? "Hide catalog" : "Browse marketplace"}
                  </Button>
                </div>
                {renderShelf(featuredFallback, "No featured Circatrons yet.")}
              </div>
              {browseOpen ? (
                <div className="space-y-3">
                  <div>
                    <h4 className="text-sm font-semibold">All Circatrons</h4>
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      Published Circa Circatrons. Install adds an integration you
                      can manage below.
                    </p>
                  </div>
                  {renderShelf(allCards, "No published Circatrons yet.")}
                </div>
              ) : null}
            </>
          );
        })()}
        {installingPackId && installPackDetail ? (
          <CircatronPackInstallWizard
            orgId={orgId}
            accessToken={accessToken}
            pack={installPackDetail}
            channels={channels}
            teams={teams}
            onCancel={() => {
              setInstallingPackId(null);
              setInstallPackDetail(null);
            }}
            onInstalled={onPackInstalled}
          />
        ) : null}
      </section>

      <section className="space-y-6">
        <div>
          <h4 className="text-sm font-semibold">Your integrations</h4>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Active Circatrons and custom webhooks for this workplace.
          </p>
        </div>

        <div ref={appsSectionRef} className="space-y-3">
          <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Apps
          </h5>
          {orgActiveInstallations.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No apps installed yet. Install Echo from Featured.
            </p>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border/80">
              {orgActiveInstallations.map((inst) => (
                <li
                  key={inst.installation_id}
                  className="px-3 py-3 space-y-3 text-sm"
                >
                  {editingInstallId === inst.installation_id ? (
                    <form onSubmit={saveEditInstall} className="space-y-3">
                      <div className="space-y-1">
                        <Label htmlFor={`edit-inst-name-${inst.installation_id}`}>
                          Display name
                        </Label>
                        <Input
                          id={`edit-inst-name-${inst.installation_id}`}
                          value={editInstallName}
                          onChange={(e) => setEditInstallName(e.target.value)}
                        />
                      </div>
                      <div className="space-y-1">
                        <Label htmlFor={`edit-inst-ch-${inst.installation_id}`}>
                          Channel
                        </Label>
                        <select
                          id={`edit-inst-ch-${inst.installation_id}`}
                          className="flex h-9 w-full rounded-md border border-input bg-background px-3 text-sm"
                          value={editInstallChannelId}
                          onChange={(e) =>
                            setEditInstallChannelId(e.target.value)
                          }
                          required
                        >
                          <option value="">Select channel…</option>
                          {channels.map((c) => (
                            <option
                              key={c.conversation_id}
                              value={String(c.conversation_id)}
                            >
                              {c.title ||
                                c.team_name ||
                                `Channel ${c.conversation_id}`}
                            </option>
                          ))}
                        </select>
                      </div>
                      <div className="space-y-3">
                        <Label>Scopes</Label>
                        <div className="flex flex-col gap-2">
                          {CIRCATRON_SCOPE_PRESETS.map((p) => (
                            <label
                              key={p.id}
                              className="flex items-start gap-2 text-sm"
                            >
                              <input
                                type="radio"
                                name={`scope-preset-${inst.installation_id}`}
                                className="mt-1"
                                checked={editInstallScopePreset === p.id}
                                onChange={() => applyEditInstallPreset(p.id)}
                              />
                              <span>
                                <span className="font-medium">{p.label}</span>
                                <span className="block text-xs text-muted-foreground">
                                  {p.description}
                                </span>
                              </span>
                            </label>
                          ))}
                          <label className="flex items-start gap-2 text-sm">
                            <input
                              type="radio"
                              name={`scope-preset-${inst.installation_id}`}
                              className="mt-1"
                              checked={editInstallScopePreset === "custom"}
                              onChange={() => applyEditInstallPreset("custom")}
                            />
                            <span>
                              <span className="font-medium">Custom</span>
                              <span className="block text-xs text-muted-foreground">
                                Pick individual scopes below
                              </span>
                            </span>
                          </label>
                        </div>
                        <div className="flex flex-col gap-2 pl-1 border-l border-border/60">
                          {APP_SCOPE_OPTIONS.map((sc) => (
                            <label
                              key={sc.id}
                              className="flex items-center gap-2 text-sm"
                            >
                              <input
                                type="checkbox"
                                checked={editInstallScopes.includes(sc.id)}
                                onChange={() => toggleEditInstallScope(sc.id)}
                              />
                              {sc.label}
                              <span className="text-xs text-muted-foreground">
                                ({sc.id})
                              </span>
                            </label>
                          ))}
                        </div>
                      </div>
                      {scopesNeedTeams(editInstallScopes) ? (
                        <div className="space-y-2">
                          <Label>Allowed teams</Label>
                          {teams.length === 0 ? (
                            <p className="text-xs text-muted-foreground">
                              No teams available.
                            </p>
                          ) : (
                            <div className="flex flex-col gap-2 max-h-40 overflow-y-auto">
                              {teams.map((t) => {
                                const id = Number(t.team_id ?? t.teamId);
                                return (
                                  <label
                                    key={id}
                                    className="flex items-center gap-2 text-sm"
                                  >
                                    <input
                                      type="checkbox"
                                      checked={editInstallTeamIds.includes(id)}
                                      onChange={() => toggleEditInstallTeam(id)}
                                    />
                                    {t.name || `Team ${id}`}
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ) : null}
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="submit"
                          size="sm"
                          disabled={savingInstall}
                        >
                          {savingInstall ? "Saving…" : "Save"}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingInstallId(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </form>
                  ) : (
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium truncate">
                          {inst.bot?.display_name || inst.provider_id}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Provider {inst.provider_id}
                          {inst.pack_id ? ` · pack ${inst.pack_id}` : ""}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          Channel {channelLabel(inst)}
                        </p>
                        <p className="text-xs text-muted-foreground break-words">
                          Scopes: {(inst.scopes || []).join(", ") || "—"}
                        </p>
                        {(inst.team_ids || []).length ? (
                          <p className="text-xs text-muted-foreground break-words">
                            Teams: {(inst.team_ids || []).join(", ")}
                          </p>
                        ) : null}
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => startEditInstall(inst)}
                        >
                          Edit
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => rotateInstallBotToken(inst)}
                        >
                          Rotate token
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => rotateInstallSigningSecret(inst)}
                        >
                          Rotate secret
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => revokeInstallation(inst)}
                        >
                          Revoke
                        </Button>
                      </div>
                    </div>
                  )}
                  <AvatarSourceEditor
                    label={`Picture for ${
                      inst.bot?.display_name || inst.provider_id || "bot"
                    }`}
                    currentUrl={resolveAvatarUrl(inst.bot?.avatar_url)}
                    previewFallback={
                      <span className="text-xs font-medium">
                        {(inst.bot?.display_name || "B")
                          .charAt(0)
                          .toUpperCase()}
                      </span>
                    }
                    onSaveUrl={async (url) => {
                      const updated = await setCircatronInstallationAvatarUrl(
                        accessToken,
                        orgId,
                        inst.installation_id,
                        url
                      );
                      patchInstallInList(updated);
                      toastSuccess("Bot picture updated");
                    }}
                    onUploadFile={async (file) => {
                      const updated = await uploadCircatronInstallationAvatar(
                        accessToken,
                        orgId,
                        inst.installation_id,
                        file
                      );
                      patchInstallInList(updated);
                      toastSuccess("Bot picture updated");
                    }}
                    onClear={async () => {
                      const updated = await clearCircatronInstallationAvatar(
                        accessToken,
                        orgId,
                        inst.installation_id
                      );
                      patchInstallInList(updated);
                      toastSuccess("Bot picture cleared");
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
          {personalActiveInstallations.length > 0 ? (
            <div className="space-y-2 pt-2">
              <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
                Member personal bots
              </h5>
              <p className="text-xs text-muted-foreground">
                Read-only for admins — revoke if needed. Owners manage tokens
                under Profile → My bots.
              </p>
              <ul className="divide-y divide-border rounded-lg border border-border/80">
                {personalActiveInstallations.map((inst) => (
                  <li
                    key={inst.installation_id}
                    className="px-3 py-3 flex flex-wrap items-center justify-between gap-3 text-sm"
                  >
                    <div>
                      <p className="font-medium">
                        {inst.bot?.display_name || inst.provider_id}
                        <span className="ml-2 text-xs font-normal text-muted-foreground">
                          personal
                        </span>
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Owner {inst.owner_user_id || "—"} · {inst.provider_id}
                      </p>
                    </div>
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      onClick={() => revokeInstallation(inst)}
                    >
                      Revoke
                    </Button>
                  </li>
                ))}
              </ul>
            </div>
          ) : null}
        </div>

        <div className="space-y-3">
          <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Event webhooks
          </h5>
          {loading && outbound.length === 0 ? (
            <p className="text-sm text-muted-foreground">Loading…</p>
          ) : outbound.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No event webhooks yet.
            </p>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border/80">
              {outbound.map((ep) => (
                <li key={ep.endpoint_id} className="px-3 py-3 space-y-2 text-sm">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="font-medium truncate">
                        {ep.name || "Event webhook"}
                        {!ep.enabled ? (
                          <span className="ml-2 text-xs text-muted-foreground">
                            (paused)
                          </span>
                        ) : null}
                      </p>
                      <p className="text-xs text-muted-foreground break-all">
                        {ep.target_url}
                      </p>
                      <p className="text-xs text-muted-foreground mt-1">
                        Events: {(ep.events || []).join(", ") || "—"}
                      </p>
                      <p className="text-xs text-muted-foreground break-words">
                        Last delivery: {formatLastDelivery(ep.last_delivery)}
                      </p>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => toggleOutbound(ep)}
                      >
                        {ep.enabled ? "Pause" : "Enable"}
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        onClick={() => rotateOutbound(ep)}
                      >
                        Rotate secret
                      </Button>
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => deleteOutbound(ep)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="space-y-3">
          <h5 className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Channel webhooks
          </h5>
          {incoming.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No channel webhooks yet.
            </p>
          ) : (
            <ul className="divide-y divide-border rounded-lg border border-border/80">
              {incoming.map((wh) => (
                <li key={wh.webhook_id} className="px-3 py-3 space-y-3 text-sm">
                  {editingWebhookId === wh.webhook_id ? (
                    <form onSubmit={saveEditIncoming} className="space-y-2">
                      <div className="space-y-1">
                        <Label htmlFor={`edit-name-${wh.webhook_id}`}>
                          Display name
                        </Label>
                        <Input
                          id={`edit-name-${wh.webhook_id}`}
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                        />
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button type="submit" size="sm" disabled={savingEdit}>
                          {savingEdit ? "Saving…" : "Save"}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingWebhookId(null)}
                        >
                          Cancel
                        </Button>
                      </div>
                    </form>
                  ) : (
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="min-w-0">
                        <p className="font-medium truncate">{wh.name}</p>
                        <p className="text-xs text-muted-foreground">
                          Posts to {channelLabel(wh)}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-2">
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={testingWebhookId === wh.webhook_id}
                          onClick={() => testIncoming(wh)}
                        >
                          {testingWebhookId === wh.webhook_id
                            ? "Sending…"
                            : "Send test"}
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => startEditIncoming(wh)}
                        >
                          Edit name
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => rotateIncoming(wh)}
                        >
                          Rotate
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="ghost"
                          onClick={() => revokeIncoming(wh)}
                        >
                          Revoke
                        </Button>
                      </div>
                    </div>
                  )}
                  <AvatarSourceEditor
                    label={`Picture for ${wh.name || "bot"}`}
                    currentUrl={resolveAvatarUrl(wh.avatar_url)}
                    previewFallback={
                      <span className="text-xs font-medium">
                        {(wh.name || "B").charAt(0).toUpperCase()}
                      </span>
                    }
                    onSaveUrl={async (url) => {
                      const updated = await setIncomingWebhookAvatarUrl(
                        accessToken,
                        orgId,
                        wh.webhook_id,
                        url
                      );
                      patchIncomingInList(updated);
                      toastSuccess("Bot picture updated");
                    }}
                    onUploadFile={async (file) => {
                      const updated = await uploadIncomingWebhookAvatar(
                        accessToken,
                        orgId,
                        wh.webhook_id,
                        file
                      );
                      patchIncomingInList(updated);
                      toastSuccess("Bot picture updated");
                    }}
                    onClear={async () => {
                      const updated = await clearIncomingWebhookAvatar(
                        accessToken,
                        orgId,
                        wh.webhook_id
                      );
                      patchIncomingInList(updated);
                      toastSuccess("Bot picture cleared");
                    }}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>
      </section>

      <section className="space-y-3">
        <div>
          <h4 className="text-sm font-semibold">Create your own</h4>
          <p className="mt-0.5 text-xs text-muted-foreground">
            Build a custom channel webhook or event webhook without a featured
            Circatron.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => setCreateIncomingOpen(true)}
          >
            Channel webhook
          </Button>
          <Button
            type="button"
            variant="outline"
            onClick={() => setCreateOutboundOpen(true)}
          >
            Event webhook
          </Button>
        </div>
      </section>

      <Dialog
        open={Boolean(viewPackDetail)}
        onOpenChange={(open) => {
          if (!open) setViewPackDetail(null);
        }}
      >
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto">
          {viewPackDetail ? (
            <>
              <DialogHeader>
                <DialogTitle>{viewPackDetail.title}</DialogTitle>
                <DialogDescription>
                  {viewPackDetail.summary}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-3 text-sm">
                {viewPackDetail.default_name ? (
                  <p className="text-xs text-muted-foreground">
                    Default name: {viewPackDetail.default_name}
                  </p>
                ) : null}
                {Array.isArray(viewPackDetail.default_events) &&
                viewPackDetail.default_events.length > 0 ? (
                  <p className="text-xs text-muted-foreground">
                    Default events:{" "}
                    {viewPackDetail.default_events.join(", ")}
                  </p>
                ) : null}
                {viewPackDetail.docs_markdown ? (
                  <div className="rounded-md border border-border/80 bg-muted/20 px-3 py-3 max-h-[50vh] overflow-y-auto text-muted-foreground">
                    <MarkdownBody
                      content={viewPackDetail.docs_markdown}
                      compact
                    />
                  </div>
                ) : null}
              </div>
              <DialogFooter className="gap-2 sm:gap-2">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setViewPackDetail(null)}
                >
                  Close
                </Button>
                <Button
                  type="button"
                  onClick={() => startPackInstall(viewPackDetail)}
                >
                  Install
                </Button>
              </DialogFooter>
            </>
          ) : null}
        </DialogContent>
      </Dialog>

      <Dialog
        open={createIncomingOpen}
        onOpenChange={setCreateIncomingOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Channel webhook</DialogTitle>
            <DialogDescription>
              Get a secret URL that posts into a channel as a bot.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={createIncoming} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="in-name">Display name</Label>
              <Input
                id="in-name"
                value={inName}
                onChange={(e) => setInName(e.target.value)}
                placeholder="Deploy bot"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="in-channel">Channel</Label>
              <select
                id="in-channel"
                className="flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm text-foreground shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring [&_option]:bg-background [&_option]:text-foreground"
                value={inChannelId}
                onChange={(e) => setInChannelId(e.target.value)}
                required
              >
                <option value="">Select channel…</option>
                {channels.map((c) => (
                  <option key={c.conversation_id} value={c.conversation_id}>
                    {c.title || `#${c.conversation_id}`}
                    {c.team_name ? ` · ${c.team_name}` : ""}
                  </option>
                ))}
              </select>
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreateIncomingOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={creatingIn}>
                {creatingIn ? "Creating…" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog
        open={createOutboundOpen}
        onOpenChange={setCreateOutboundOpen}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Event webhook</DialogTitle>
            <DialogDescription>
              Circa will notify your HTTPS URL when selected workplace events
              happen.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={createOutbound} className="space-y-3">
            <div className="space-y-1.5">
              <Label htmlFor="out-name">Name</Label>
              <Input
                id="out-name"
                value={outName}
                onChange={(e) => setOutName(e.target.value)}
                placeholder="My automation"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="out-url">Destination URL</Label>
              <Input
                id="out-url"
                value={outUrl}
                onChange={(e) => setOutUrl(e.target.value)}
                placeholder="https://…"
                required
              />
            </div>
            <div className="space-y-2">
              <Label>Events</Label>
              <div className="flex flex-wrap gap-3">
                {EVENT_OPTIONS.map((ev) => (
                  <label
                    key={ev.id}
                    className="flex items-center gap-2 text-sm text-foreground"
                  >
                    <input
                      type="checkbox"
                      checked={outEvents.includes(ev.id)}
                      onChange={() => toggleEvent(ev.id)}
                    />
                    {ev.label}
                  </label>
                ))}
              </div>
            </div>
            <DialogFooter className="gap-2 sm:gap-2">
              <Button
                type="button"
                variant="outline"
                onClick={() => setCreateOutboundOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={creatingOut}>
                {creatingOut ? "Creating…" : "Create"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default OrgConnectorsPanel;
